/**
 * @fileoverview Cost vs Profit donut chart component with glassmorphism styling.
 * @module shared/components/ui/CostVsProfitDonut
 */

import { ReactElement, useMemo, useCallback } from 'react'
import { ChartCardHeader } from './ChartCardHeader'
import { ChartPie } from 'lucide-react'
import { ChartEmptyState } from './ChartEmptyState'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { Cell, Label, Pie, PieChart } from 'recharts'
import { Card } from '@/components/ui/card'
import { ChartContainer, ChartTooltip } from '@/components/ui/chart'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { Skeleton } from '@/shared/components/ui'
import { useCostBreakdown } from '@/shared/hooks/useCostBreakdown'
import { getDonutChartColors, formatPercentage } from '@/components/ui/chart-utils'
import { cn } from '@/shared/utils/cn'
import { useCompactMoney } from '@/shared/hooks/useCompactMoney'
import { useFilter } from '@/shared/hooks/useFilter'
import type { ChartConfig } from '@/components/ui/chart'
import { useAggregatedProductMetrics } from '@/shared/hooks/dashboard/useAggregatedProductMetrics'
import { useProductGroupsMetadata } from '@/shared/hooks'
import { useDonutTouchInteraction, renderStaticActiveShape } from '@/shared/hooks/useDonutTouchInteraction'
import { renderActiveDonutSector } from '@/shared/components/ui/AnimatedDonutSector'
import { DonutSliceGradients } from '@/shared/components/ui/DonutSliceGradients'
import { donutSliceGradientFill } from '@/shared/utils/donutGradient'
import { AnchoredHoverCard } from './AnchoredHoverCard'
import { Thumbnail } from './Thumbnail'
import { RechartsHoverAdapter } from './RechartsHoverAdapter'
import { useDashboardData } from '@/shared/hooks/dashboard/useDashboardData'

/**
 * Chart data point for cost breakdown visualization
 */
interface CostBreakdownData {
  /** Metric name */
  name: string
  /** Value amount */
  value: number
  /** Fill color for the pie slice */
  fill: string
  /** Percentage value */
  percentage: number
  /** Index signature for recharts compatibility */
  [key: string]: unknown
}

/**
 * Props for CostVsProfitDonut component
 */
interface CostVsProfitDonutProps {
  /** Additional CSS classes */
  className?: string
}

/**
 * Creates chart configuration for ShadCN charts
 */
const createChartConfig = (t: TFunction<'features-home'>, profitBasis: string): ChartConfig => ({
  cogs: {
    label: t('metricLabels.cogs'),
    color: 'var(--chart-legend-cogs)',
  },
  adSpend: {
    label: t('metricLabels.adSpend'),
    color: 'var(--chart-legend-adspend)',
  },
  ...(profitBasis === 'OC'
    ? {
      overhead: {
        label: t('businessCosts.title', { defaultValue: 'Business Costs' }),
        color: 'var(--chart-legend-overhead, var(--chart-legend-cogs))',
      },
    }
    : {}),
  profit: {
    label: t('metricLabels.profit', { defaultValue: 'Profit' }),
    color: profitBasis === 'OC' ? 'var(--chart-legend-oc)' : 'var(--chart-legend-profit)',
  },
})

/**
 * Tooltip item for cost breakdown
 */
interface CostTooltipItem {
  key: string
  name: string
  icon?: ReactElement | undefined
  value: number
  percentage?: string | undefined
}

/**
 * Builds the table-based tooltip content for CostVsProfitDonut
 */
function CostTooltipContent({
  sliceName,
  sliceValue,
  sliceColor,
  items,
  formatValue,
}: {
  sliceName: string
  sliceValue: number
  sliceColor: string
  items: CostTooltipItem[]
  formatValue: (value: number, maxDigits?: number) => string
}): ReactElement {
  const { t } = useTranslation('features-home')

  if (!items.length) {
    return (
      <div className="text-base font-medium text-center p-4 text-muted-foreground">
        {t('charts.costBreakdown.noItemsFor', {
          defaultValue: 'No items for {{name}}',
          name: sliceName,
        })}
      </div>
    )
  }

  return (
    <Table className="table-fixed">
      <TableHeader>
        <TableRow className="hover:bg-transparent border-t-0 bg-black/10">
          <TableHead className="pl-4 pr-2 h-8">
            <span className="text-xs font-medium text-muted-foreground">
              {sliceName}
            </span>
          </TableHead>
          <TableHead className="w-36 pl-2 pr-4 h-8 text-right">
            <div className="flex items-center justify-end gap-1">
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: sliceColor }}
              />
              <span className="text-xs font-semibold text-foreground tabular-nums">
                {formatValue(sliceValue)}
              </span>
            </div>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item) => (
          <TableRow key={item.key} className="hover:bg-transparent">
            <TableCell className="pl-4 pr-2 py-2 align-middle">
              <div className="flex items-center gap-2 min-w-0">
                {item.icon && <span className="shrink-0">{item.icon}</span>}
                <span className="truncate text-sm text-muted-foreground">{item.name}</span>
              </div>
            </TableCell>
            <TableCell className="w-36 pl-2 pr-4 py-2 text-right align-middle">
              <div className="flex flex-col items-end">
                <span className="text-sm font-medium tabular-nums text-foreground whitespace-nowrap">
                  {formatValue(item.value)}
                </span>
                {item.percentage && (
                  <span className="text-xs text-muted-foreground/60">
                    {item.percentage}
                  </span>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

/**
 * Cost vs Profit donut chart component displaying COGS, Ad Spend, and Profit breakdown.
 * Matches the mockup design with center labels and legend below.
 *
 * @component
 * @example
 * ```tsx
 * <CostVsProfitDonut className="w-full" />
 * ```
 */
export function CostVsProfitDonut({ className }: CostVsProfitDonutProps): ReactElement {
  const { t } = useTranslation('features-home')
  // Important: read all hooks first before any early returns to keep hook order stable
  const { data, isLoading, error } = useCostBreakdown()

  const { profitBasis } = useFilter()
  const compactMoney = useCompactMoney()
  const profitLabel = useMemo(
    () => t('metricLabels.profit', { defaultValue: 'Profit' }),
    [t]
  )
  const businessCostsLabel = useMemo(
    () => t('businessCosts.title', { defaultValue: 'Business Costs' }),
    [t]
  )

  // Touch interaction hook for mobile/tablet
  const {
    isMobile,
    isTablet,
    isBelowDesktop,
    mobileSlice,
    tabletSlice,
    tabletAnchor,
    chartContainerRef,
    handleSliceClick,
    clearMobileSlice,
  } = useDonutTouchInteraction<CostBreakdownData>()
  const formatValue = useCallback(
    (value: number, maxDigits = 1): string => compactMoney(value, maxDigits),
    [compactMoney]
  )

  // Provide stable primitive fallbacks so hooks (useMemo) can be called
  // on every render in the same order regardless of loading / error states.
  const safeCogs = data?.cogs ?? 0
  const safeAdSpend = data?.adSpend ?? 0
  const safeProfit = data?.profit ?? 0

  const colors = getDonutChartColors()
  // Aggregated product metrics used for hovercard item lists
  const { products } = useAggregatedProductMetrics()
  const { getGroup } = useProductGroupsMetadata()
  const { salesTimeQuery } = useDashboardData()

  type MetricKey = 'adSpend' | 'cogs' | 'profit' | 'overhead'

  const resolveMetricKey = useCallback(
    (sliceName: string): MetricKey => {
      if (sliceName === t('metricLabels.adSpend', { defaultValue: 'Ad Spend' })) return 'adSpend'
      if (sliceName === profitLabel) return 'profit'
      if (sliceName === businessCostsLabel) return 'overhead'
      return 'cogs'
    },
    [businessCostsLabel, profitLabel, t]
  )

  const metricColors = useMemo<Record<MetricKey, string>>(
    () => ({
      cogs: 'var(--chart-legend-cogs)',
      adSpend: 'var(--chart-legend-adspend)',
      profit: profitBasis === 'OC' ? 'var(--chart-legend-oc)' : 'var(--chart-legend-profit)',
      overhead: 'var(--chart-legend-overhead)',
    }),
    [profitBasis]
  )

  const buildCostTooltipData = useCallback(
    (slice: CostBreakdownData) => {
      const sliceName = slice.name
      const sliceValue = slice.value
      const metricKey = resolveMetricKey(sliceName)
      const sliceColor = metricColors[metricKey]

      const indirectCostsGroups = salesTimeQuery.data?.indirectCosts?.groups ?? []
      const indirectCostsOtherTotal = Number(salesTimeQuery.data?.indirectCosts?.otherTotal ?? 0)

      let items: CostTooltipItem[] = []
      if (metricKey === 'overhead') {
        const topGroups = indirectCostsGroups.slice(0, 8)
        items = topGroups.map((g, idx) => {
          const baseLabel =
            (g.subcategoryDisplayName && g.subcategoryDisplayName.trim()) ||
            (g.label && g.label.trim()) ||
            t('operatingOverhead.unknown', { defaultValue: 'Unnamed subcategory' })
          const percentage =
            typeof g.sharePercent === 'number'
              ? formatPercentage(g.sharePercent)
              : sliceValue > 0
                ? formatPercentage((g.total / sliceValue) * 100)
                : undefined
          return {
            key: `overhead-${idx}`,
            name: baseLabel,
            value: g.total,
            percentage,
          }
        })
        if (items.length < 8 && indirectCostsOtherTotal > 0) {
          const othersShare = sliceValue > 0 ? (indirectCostsOtherTotal / sliceValue) * 100 : 0
          items.push({
            key: 'overhead-others',
            name: t('operatingOverhead.others', { defaultValue: 'Others' }),
            value: indirectCostsOtherTotal,
            percentage: othersShare > 0 ? formatPercentage(othersShare) : undefined,
          })
        }
      } else {
        const ranked = [...products]
          .sort((a, b) => (b[metricKey] as number) - (a[metricKey] as number))
          .filter(row => (row[metricKey] as number) > 0)
          .slice(0, 8)

        items = ranked.map((row) => {
          const val =
            metricKey === 'adSpend' ? row.adSpend : metricKey === 'cogs' ? row.cogs : row.profit
          const percentage = sliceValue > 0 ? formatPercentage((val / sliceValue) * 100) : undefined
          const group = getGroup(row.sku)
          const img = group?.imageUrl ?? null
          return {
            key: row.sku,
            name: row.name,
            icon: <Thumbnail src={img} alt={row.name} size={40} />,
            value: val,
            percentage,
          }
        })
      }

      return { sliceColor, items }
    },
    [getGroup, metricColors, products, resolveMetricKey, salesTimeQuery.data, t]
  )

  // Create chart config for ShadCN
  const chartConfig = useMemo(() => createChartConfig(t, profitBasis), [t, profitBasis])

  // Transform data for Recharts format
  const chartData: CostBreakdownData[] = useMemo(() => {
    const base: CostBreakdownData[] = []

    // Only show COGS if non-zero
    if (safeCogs > 0) {
      base.push({
        name: t('metricLabels.cogs'),
        value: safeCogs,
        fill: colors.cogs,
        percentage: data?.percentages?.cogs ?? 0,
      })
    }

    // Only show ad spend if non-zero
    if (safeAdSpend > 0) {
      base.push({
        name: t('metricLabels.adSpend'),
        value: safeAdSpend,
        fill: colors.adSpend,
        percentage: data?.percentages?.adSpend ?? 0,
      })
    }

    // Only show business costs if non-zero (and OC mode)
    const overhead = Number(data?.overhead ?? 0)
    if (profitBasis === 'OC' && overhead > 0) {
      base.push({
        name: t('businessCosts.title', { defaultValue: 'Business Costs' }),
        value: overhead,
        fill: colors.overhead,
        percentage: Number(data?.percentages?.overhead ?? 0),
      })
    }

    // Only show profit slice when positive
    if (safeProfit > 0) {
      base.push({
        name: t('metricLabels.profit', { defaultValue: 'Profit' }),
        value: safeProfit,
        fill: profitBasis === 'OC' ? 'var(--chart-legend-oc)' : colors.profit,
        percentage: data?.percentages?.profit ?? 0,
      })
    }

    return base
  }, [safeCogs, safeAdSpend, safeProfit, colors, t, profitBasis, data?.overhead, data?.percentages])

  // Now handle loading / error / empty states AFTER hooks/memos so hook order stays stable.
  if (isLoading) {
    return (
      <Card className={cn('rounded-xl border border-border/40 bg-card/30 overflow-hidden', className)}>
        <ChartCardHeader
          title={t('charts.costBreakdown.title', { defaultValue: 'Costs' })}
          href="/dashboard/performance?columns=cogs,adSpend,profit,sales&sort=sales:desc"
        />
        <div className="px-6 pt-4 pb-8 space-y-8">
          <div className="flex justify-center">
            <Skeleton className="h-64 w-64 rounded-full" />
          </div>
          <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
          </div>
        </div>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className={cn('rounded-xl border border-border/40 bg-card/30 overflow-hidden', className)}>
        <ChartCardHeader
          title={t('charts.costBreakdown.title', { defaultValue: 'Costs' })}
          href="/dashboard/performance?columns=cogs,adSpend,profit,sales&sort=sales:desc"
        />
        <div className="px-6 pt-4 pb-8">
          <div className="flex items-center justify-center h-48 text-destructive">
            <p>{t('charts.costBreakdown.error')}</p>
          </div>
        </div>
      </Card>
    )
  }

  // Consistency with other charts: treat all-zero slices as "no data"
  const isAllZero = safeCogs === 0 && safeAdSpend === 0 && safeProfit === 0

  if (!data || isAllZero) {
    return (
      <Card className={cn('rounded-xl border border-border/40 bg-card/30 overflow-hidden', className)}>
        <ChartCardHeader
          title={t('charts.costBreakdown.title', { defaultValue: 'Costs' })}
          href="/dashboard/performance?columns=cogs,adSpend,profit,sales&sort=sales:desc"
        />
        <div className="px-6 pt-4 pb-8">
          <ChartEmptyState
            variant="donut"
            icon={ChartPie}
            message={t('charts.costBreakdown.noData')}
            hint={t('charts.emptyHint')}
            className="h-48"
          />
        </div>
      </Card>
    )
  }

  return (
    <Card className={cn('rounded-xl border border-border/40 bg-card/30 overflow-hidden', className)}>
      {/* Header section */}
      <ChartCardHeader
        title={t('charts.costBreakdown.title', { defaultValue: 'Costs' })}
        href="/dashboard/performance?columns=cogs,adSpend,profit,sales&sort=sales:desc"
      />
      {/* Chart section with border separator */}
      <div ref={chartContainerRef} className="px-6 pt-4 pb-8">
        {/* Donut Chart with Center Labels */}
        <ChartContainer
          id="cost-vs-profit-donut"
          config={chartConfig}
          className="mx-auto aspect-[1/1] w-full [&_.recharts-surface]:overflow-visible"
        >
          <PieChart margin={{ top: 20, left: 20, right: 20, bottom: 20 }}>
            <DonutSliceGradients idPrefix="cost-vs-profit-slice" colors={chartData.map((d) => d.fill)} />
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius="55%"
              outerRadius="70%"
              paddingAngle={4}
              cornerRadius={8}
              dataKey="value"
              stroke="none"
              isAnimationActive={false}
              activeShape={isBelowDesktop ? renderStaticActiveShape : renderActiveDonutSector}
              onClick={(_, index) => {
                const slice = chartData[index]
                if (slice) handleSliceClick(slice)
              }}
              labelLine={{
                    stroke: 'var(--muted-foreground)',
                    strokeWidth: 1,
                    strokeDasharray: '3 3',
                  }}
              label={({ cx, cy, midAngle = 0, outerRadius, payload }) => {
                const RADIAN = Math.PI / 180
                const valueText = formatValue(payload.value)
                const radius = Number(outerRadius) + (isBelowDesktop ? 16 : 20)
                const x = cx + radius * Math.cos(-midAngle * RADIAN)
                const y = cy + radius * Math.sin(-midAngle * RADIAN)
                const isRightSide = Math.cos(-midAngle * RADIAN) > 0

                if (isBelowDesktop) {
                  const metricKey = resolveMetricKey(String(payload.name))
                  const shortLabelMap: Record<MetricKey, string> = {
                    cogs: t('metricLabels.short.cogs', { defaultValue: 'COGS' }),
                    adSpend: t('metricLabels.short.adSpend', { defaultValue: 'Ads' }),
                    overhead: t('metricLabels.short.overhead', { defaultValue: 'Biz costs' }),
                    profit: t('metricLabels.short.profit', { defaultValue: 'Profit' }),
                  }
                  const shortLabel = shortLabelMap[metricKey]
                  return (
                    <g>
                      <text
                        x={x}
                        y={y - 6}
                        textAnchor={isRightSide ? 'start' : 'end'}
                        dominantBaseline="middle"
                        className="font-bold fill-foreground"
                        style={{ fontSize: 14 }}
                      >
                        {valueText}
                      </text>
                      <text
                        x={x}
                        y={y + 12}
                        textAnchor={isRightSide ? 'start' : 'end'}
                        className="fill-muted-foreground"
                        style={{ fontSize: 12 }}
                      >
                        {shortLabel}
                      </text>
                    </g>
                  )
                }

                const labelText = `${payload.name} ${payload.percentage.toFixed(1)}%`
                const MAX_LENGTH = 18
                const shouldWrap = labelText.length > MAX_LENGTH

                let line1 = labelText
                let line2 = ''

                if (shouldWrap) {
                  const words = payload.name.split(' ')
                  const percentage = `${payload.percentage.toFixed(1)}%`

                  if (words.length > 1) {
                    line1 = payload.name
                    line2 = percentage
                  } else {
                    const mid = Math.floor(labelText.length / 2)
                    const spaceIndex = labelText.lastIndexOf(' ', mid)
                    if (spaceIndex > 0) {
                      line1 = labelText.substring(0, spaceIndex)
                      line2 = labelText.substring(spaceIndex + 1)
                    }
                  }
                }

                return (
                  <g>
                    <text
                      x={x}
                      y={shouldWrap ? y - 21 : y - 15}
                      textAnchor={isRightSide ? 'start' : 'end'}
                      dominantBaseline="middle"
                      className="font-bold fill-foreground"
                      style={{ fontSize: 16 }}
                    >
                      {valueText}
                    </text>
                    <g transform={`translate(${x}, ${shouldWrap ? y + 9 : y + 5})`}>
                      <circle
                        cx={isRightSide ? 5 : -5}
                        cy={shouldWrap ? -6 : 0}
                        r={3}
                        fill={payload.fill}
                      />
                      <text
                        x={isRightSide ? 12 : -12}
                        textAnchor={isRightSide ? 'start' : 'end'}
                        className="fill-muted-foreground"
                        style={{ fontSize: 13 }}
                      >
                        <tspan x={isRightSide ? 12 : -12} dy={shouldWrap ? '-0.6em' : '0'} dominantBaseline="middle">
                          {line1}
                        </tspan>
                        {shouldWrap && (
                          <tspan x={isRightSide ? 12 : -12} dy="1.2em" dominantBaseline="middle">
                            {line2}
                          </tspan>
                        )}
                      </text>
                    </g>
                  </g>
                )
              }}
                >
                  {chartData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={donutSliceGradientFill('cost-vs-profit-slice', index)} />
                  ))}
                  <Label
                    content={({ viewBox }) => {
                      if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                        const profitPercentage = data.percentages.profit
                        const isProfit = safeProfit > 0

                        return (
                          <text
                            x={viewBox.cx}
                            y={viewBox.cy}
                            textAnchor="middle"
                            dominantBaseline="middle"
                          >
                            <tspan
                              x={viewBox.cx}
                              y={(viewBox.cy || 0) - 10}
                              className={cn('font-bold', isBelowDesktop ? 'text-xl' : 'text-3xl')}
                              fill={isProfit ? 'var(--color-positive)' : 'var(--color-negative)'}
                            >
                              {isProfit
                                ? `+${profitPercentage.toFixed(1)}%`
                                : formatValue(Math.abs(safeProfit))}
                            </tspan>
                            <tspan
                              x={viewBox.cx}
                              y={(viewBox.cy || 0) + 16}
                              className={cn(isBelowDesktop ? 'text-sm' : 'text-base')}
                              fill="var(--muted-foreground)"
                            >
                              {isProfit
                                ? t('metricLabels.margin')
                                : t('metricLabels.loss', { defaultValue: 'Loss' })}
                            </tspan>
                          </text>
                        )
                      }
                    }}
                  />
                </Pie>
                <ChartTooltip
                  cursor={false}
                  content={
                    <RechartsHoverAdapter
                      dataChartId="chart-cost-vs-profit-donut"
                      width={450}
                      estimatedHeight={420}
                      disabled={isBelowDesktop}
                      animatePosition={false}
                    >
                      {({ payload }) => {
                        type SlicePayload = { value?: number; name?: string }
                        const p: SlicePayload | null =
                          Array.isArray(payload) && payload.length
                            ? (payload[0] as SlicePayload)
                            : null
                        if (!p) return null

                        const sliceName = String(p.name ?? '')
                        const sliceValue = Number(p.value ?? 0)
                        if (!sliceName || sliceValue === 0) return null

                        const profitLabel = t('metricLabels.profit', { defaultValue: 'Profit' })

                        // Determine metric key and color
                        let metricKey: 'adSpend' | 'cogs' | 'profit' | 'overhead' = 'cogs'
                        if (sliceName === t('metricLabels.adSpend')) metricKey = 'adSpend'
                        else if (sliceName === profitLabel) metricKey = 'profit'
                        else if (sliceName === t('businessCosts.title', { defaultValue: 'Business Costs' }))
                          metricKey = 'overhead'

                        const titleColorMap: Record<'cogs' | 'adSpend' | 'profit' | 'overhead', string> = {
                          cogs: 'var(--chart-legend-cogs)',
                          adSpend: 'var(--chart-legend-adspend)',
                          profit: profitBasis === 'OC' ? 'var(--chart-legend-oc)' : 'var(--chart-legend-profit)',
                          overhead: 'var(--chart-legend-overhead)',
                        }
                        const sliceColor = titleColorMap[metricKey]

                        // Build items based on metric type
                        const indirectCostsGroups = salesTimeQuery.data?.indirectCosts?.groups ?? []
                        const indirectCostsOtherTotal = Number(salesTimeQuery.data?.indirectCosts?.otherTotal ?? 0)

                        let items: CostTooltipItem[] = []
                        if (metricKey === 'overhead') {
                          const topGroups = indirectCostsGroups.slice(0, 8)
                          items = topGroups.map((g, idx) => {
                            const baseLabel =
                              (g.subcategoryDisplayName && g.subcategoryDisplayName.trim()) ||
                              (g.label && g.label.trim()) ||
                              t('operatingOverhead.unknown', { defaultValue: 'Unnamed subcategory' })
                            const percentage =
                              typeof g.sharePercent === 'number'
                                ? formatPercentage(g.sharePercent)
                                : sliceValue > 0
                                  ? formatPercentage((g.total / sliceValue) * 100)
                                  : undefined
                            return {
                              key: `overhead-${idx}`,
                              name: baseLabel,
                              value: g.total,
                              percentage,
                            }
                          })
                          if (items.length < 8 && indirectCostsOtherTotal > 0) {
                            const othersShare = sliceValue > 0 ? (indirectCostsOtherTotal / sliceValue) * 100 : 0
                            items.push({
                              key: 'overhead-others',
                              name: t('operatingOverhead.others', { defaultValue: 'Others' }),
                              value: indirectCostsOtherTotal,
                              percentage: othersShare > 0 ? formatPercentage(othersShare) : undefined,
                            })
                          }
                        } else {
                          const ranked = [...products]
                            .sort((a, b) => (b[metricKey] as number) - (a[metricKey] as number))
                            .filter(row => (row[metricKey] as number) > 0)
                            .slice(0, 8)

                          items = ranked.map((row) => {
                            const val =
                              metricKey === 'adSpend' ? row.adSpend : metricKey === 'cogs' ? row.cogs : row.profit
                            const percentage = sliceValue > 0 ? formatPercentage((val / sliceValue) * 100) : undefined
                            const group = getGroup(row.sku)
                            const img = group?.imageUrl ?? null
                            return {
                              key: row.sku,
                              name: row.name,
                              icon: <Thumbnail src={img} alt={row.name} size={40} />,
                              value: val,
                              percentage,
                            }
                          })
                        }

                        return (
                          <CostTooltipContent
                            sliceName={sliceName}
                            sliceValue={sliceValue}
                            sliceColor={sliceColor}
                            items={items}
                            formatValue={formatValue}
                          />
                        )
                      }}
                    </RechartsHoverAdapter>
                  }
                  wrapperStyle={{ display: 'none' }}
                />
              </PieChart>
            </ChartContainer>
      </div>

      {/* Mobile Sheet for tooltip content */}
      {isMobile && (
        <Sheet open={!!mobileSlice} onOpenChange={(open) => !open && clearMobileSlice()}>
          <SheetContent
            side="bottom"
            showCloseButton={false}
            className={cn(
              'max-h-[85vh] overflow-y-auto rounded-t-2xl',
              'bg-[var(--glass-surface-dark)]'
            )}
          >
            <SheetHeader className="sr-only">
              <SheetTitle>{mobileSlice?.name ?? 'Details'}</SheetTitle>
              <SheetDescription>Breakdown by product</SheetDescription>
            </SheetHeader>
            <div className="pb-safe">
              {mobileSlice && (() => {
                const sliceName = mobileSlice.name
                const sliceValue = mobileSlice.value
                const { sliceColor, items } = buildCostTooltipData(mobileSlice)

                return (
                  <CostTooltipContent
                    sliceName={sliceName}
                    sliceValue={sliceValue}
                    sliceColor={sliceColor}
                    items={items}
                    formatValue={formatValue}
                  />
                )
              })()}
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* Tablet popover - uses AnchoredHoverCard positioned at chart center */}
      {isTablet && tabletSlice && tabletAnchor && (() => {
        const sliceName = tabletSlice.name
        const sliceValue = tabletSlice.value
        const { sliceColor, items } = buildCostTooltipData(tabletSlice)

        return (
          <AnchoredHoverCard
            active={true}
            anchor={tabletAnchor}
            width={450}
            estimatedHeight={420}
            margin={8}
            offset={{ x: 0, y: 0 }}
            inert={false}
          >
            <CostTooltipContent
              sliceName={sliceName}
              sliceValue={sliceValue}
              sliceColor={sliceColor}
              items={items}
              formatValue={formatValue}
            />
          </AnchoredHoverCard>
        )
      })()}
    </Card>
  )
}
